// @flow
import { isAutoLoginDisabled } from 'app/packages/pinterest-authentication/utils';
import { type GoogleLoginCreds } from 'app/packages/pinterest-flow-types/authentication';
import { type GoogleSignupOptions } from 'app/packages/pinterest-flow-types/registration';
import { loadScriptWithWait, SCRIPT_TYPE } from 'app/packages/script-loader';

export const GPLUS_CONNECT = '_gplusConnect';

export const GPLUS_REDIRECT_URI = 'postmessage';
export const GPLUS_SCOPE_PROFILE_EMAIL = 'profile email';
const GPLUS_CLIENT_ID = '694505692171-31closf3bcmlt59aeulg2j81ej68j6hk.apps.googleusercontent.com';
const GPLUS_APP_PACKAGE_NAME = 'com.pinterest';
const GOOGLE_ACCESS_TYPE = 'offline';
const GOOGLE_COOKIE_POLICY = 'single_host_origin';

const injectLoginScriptWithWait = () =>
  loadScriptWithWait('https://apis.google.com/js/client:platform.js', SCRIPT_TYPE.GPLUS_ONE);

export type GoogleApiResponse = {|
  expires_at: string,
  access_token: string,
  id_token: string,
  status: {
    signed_in: boolean,
  },
  code: string,
  error?: string,
  first_name?: string,
|};

export type GoogleResponse = {|
  data: GoogleLoginCreds,
  signupOptions: GoogleSignupOptions,
|};

// $FlowFixMe Flow v0.104.0 upgrade supression.
export function initGoogleAuth2SDK(trackAction?: (type: string) => void): Promise<*> {
  const params = {
    scope: GPLUS_SCOPE_PROFILE_EMAIL,
    client_id: GPLUS_CLIENT_ID,
    app_package_name: GPLUS_APP_PACKAGE_NAME,
    access_type: GOOGLE_ACCESS_TYPE,
    cookie_policy: GOOGLE_COOKIE_POLICY,
  };

  const track = function(action) {
    if (trackAction) {
      trackAction(action);
    }
  };

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  return new Promise<*>((resolve, reject) => {
    injectLoginScriptWithWait().then(() => {
      track('google_script_load');

      if (window.gapi.auth2) {
        track('google_script_init_onuse');
        window.gapi.auth2.init(params).then(
          (googleAuth) => {
            track('google_init_success');
            resolve(googleAuth);
          },
          ({ error }) => {
            track('google_init_error');
            reject(`init_onuse_${error}`);
          },
        );
      } else {
        track('google_script_init_exists');
        window.gapi.load('auth2', {
          callback: () => {
            track('google_auth2_load_complete');
            window.gapi.auth2.init(params).then(
              (googleAuth) => {
                track('google_init_success');
                resolve(googleAuth);
              },
              ({ error }) => {
                track('google_init_error');
                reject(`init_exists_${error}`);
              },
            );
          },
          onerror: ({ error }) => {
            track('google_auth2_script_load_error');
            reject(error);
          },
          timeout: 15000, // milliseconds
          ontimeout: () => {
            track('google_auth2_script_load_timeout');
            reject('timeout');
          },
        });
      }
    });
  });
}

function _initGoogleButton(
  _googleButton: ?HTMLElement,
  autologin: ?boolean,
  gPlusScope: string,
): Promise<$Shape<GoogleResponse>> {
  return new Promise((resolve, reject) => {
    if (autologin && isAutoLoginDisabled()) {
      reject();
      return;
    }

    injectLoginScriptWithWait().then(() => {
      const _gapi = window.gapi;
      _gapi.signin.render(_googleButton, {
        scope: gPlusScope,
        clientid: GPLUS_CLIENT_ID,
        apppackagename: GPLUS_APP_PACKAGE_NAME,
        redirecturi: GPLUS_REDIRECT_URI,
        accesstype: GOOGLE_ACCESS_TYPE,
        cookiepolicy: GOOGLE_COOKIE_POLICY,
        callback: (authResult: any) => {
          const status = authResult.status;
          const result = status.signed_in
            ? {
                data: {
                  gplus_id_token: authResult.id_token,
                  gplus_access_token: authResult.access_token,
                  gplus_expires_at: authResult.expires_at,
                  gplus_autologin: autologin || false,
                },
                signupOptions: {
                  gplus_code: authResult.code,
                  gplus_id_token: authResult.id_token,
                  gplus_redirect_uri: GPLUS_REDIRECT_URI,
                },
              }
            : {};
          if (status.signed_in || autologin) {
            resolve(result);
          }
        },
      });
    });
  });
}

/*
 * Blended function, that calls the new auth2.init but then
 * uses signin.render to log the user in
 */
function _initGoogleButtonV4(
  _googleButton: ?HTMLElement,
  trackAction: (type: string) => void,
): Promise<$Shape<GoogleResponse>> {
  return new Promise((resolve, reject) => {
    if (isAutoLoginDisabled()) {
      reject();
      return;
    }
    trackAction('init_google_button_v4');
    trackAction('google_button_init');
    initGoogleAuth2SDK(trackAction).then(
      () => {
        trackAction('google_sdk_load');
        const _gapi = window.gapi;
        _gapi.signin.render(_googleButton, {
          scope: GPLUS_SCOPE_PROFILE_EMAIL,
          clientid: GPLUS_CLIENT_ID,
          apppackagename: GPLUS_APP_PACKAGE_NAME,
          redirecturi: GPLUS_REDIRECT_URI,
          accesstype: GOOGLE_ACCESS_TYPE,
          cookiepolicy: GOOGLE_COOKIE_POLICY,
          callback: (authResult: any) => {
            trackAction('callback_response');
            const status = authResult.status;
            const result = status.signed_in
              ? {
                  data: {
                    gplus_id_token: authResult.id_token,
                    gplus_access_token: authResult.access_token,
                    gplus_expires_at: authResult.expires_at,
                    gplus_autologin: true,
                  },
                  signupOptions: {
                    gplus_code: authResult.code,
                    gplus_id_token: authResult.id_token,
                    gplus_redirect_uri: GPLUS_REDIRECT_URI,
                  },
                }
              : {};
            if (status.signed_in) {
              trackAction('google_auth_data_found');
            } else {
              trackAction('gplus_user_not_logged_in');
            }
            resolve(result);
          },
        });
      },
      (error) => {
        trackAction('google_auth2_sdk_init_error');
        reject(error);
      },
    );
  });
}

/*
 * Similar function to _initGoogleButton above, except it
 * migrates away from the old G+ apis. Uses
 * gapi.signin2.render.
 *
 * Replace _initGoogleButton with this function after the
 * relevant experiments are fully ramped.
 */
function _initGoogleButtonV2(
  _googleButtonId: string,
  gapi: Object,
  trackAction: (type: string) => void,
  autologin?: boolean = false,
): Promise<$Shape<GoogleResponse>> {
  return new Promise((resolve, reject) => {
    if (autologin && isAutoLoginDisabled()) {
      reject();
      return;
    }
    trackAction('google_button_init');
    initGoogleAuth2SDK(trackAction).then(
      () => {
        trackAction('google_sdk_load');
        gapi.signin2.render(_googleButtonId, {
          scope: GPLUS_SCOPE_PROFILE_EMAIL,
          onsuccess: (googleUser) => {
            trackAction('google_button_success');
            const authResult = googleUser.getAuthResponse(true);

            const result = {
              data: {
                gplus_id_token: authResult.id_token,
                gplus_access_token: authResult.access_token,
                gplus_expires_at: authResult.expires_at,
                gplus_autologin: autologin || false,
              },
              signupOptions: {
                gplus_code: '', // no longer returned from API
                gplus_id_token: authResult.id_token,
                gplus_redirect_uri: GPLUS_REDIRECT_URI,
              },
            };
            resolve(result);
          },
          onfailure: () => {
            trackAction('google_button_failure');
            reject('gapi_signin2_render_error');
          },
        });
      },
      (error) => {
        trackAction('google_auth2_sdk_init_error');
        reject(error);
      },
    );
  });
}

function _initGoogleButtonV3(
  gapi: Object,
  trackAction: (type: string) => void,
  autologin?: boolean = false,
): Promise<$Shape<GoogleResponse>> {
  return new Promise((resolve, reject) => {
    if (autologin && isAutoLoginDisabled()) {
      reject();
      return;
    }
    // no longer a button, but keep stat the same for analysis purposes
    trackAction('google_button_init');
    initGoogleAuth2SDK(trackAction).then(
      (googleAuth) => {
        trackAction('google_sdk_load');

        const user = googleAuth.currentUser.get();
        if (!user || !user.Zi) {
          reject('gplus_user_not_found');
        }
        const authData = user.getAuthResponse(true);

        if (authData) {
          trackAction('google_auth_data_found');
          const result = {
            data: {
              gplus_id_token: authData.id_token,
              gplus_access_token: authData.access_token,
              gplus_expires_at: authData.expires_at,
              gplus_autologin: autologin || false,
            },
          };
          resolve(result);
        }
        reject('gplus_user_not_logged_in');
      },
      (error) => {
        trackAction('google_auth2_sdk_init_error');
        reject(error);
      },
    );
  });
}

export function initGoogleButtonV4(
  _googleButton: ?HTMLElement,
  trackAction: (type: string) => void,
): Promise<$Shape<GoogleResponse>> {
  return _initGoogleButtonV4(_googleButton, trackAction);
}

export function initGoogleButton(
  _googleButton: ?HTMLElement,
  autologin: ?boolean,
): Promise<$Shape<GoogleResponse>> {
  return _initGoogleButton(_googleButton, autologin, GPLUS_SCOPE_PROFILE_EMAIL);
}

/*
 * Used in conjunction with _initGoogleButtonV2 to migrate off
 * the old G+ apis. Will consolidate these down to one once the
 * migration experiments have finished.
 */
export function initGoogleButtonV2(
  _googleButtonId: string,
  gapi: Object,
  trackAction: (type: string) => void,
  autologin?: boolean = false,
): Promise<$Shape<GoogleResponse>> {
  return _initGoogleButtonV2(_googleButtonId, gapi, trackAction, autologin);
}

export function initGoogleButtonV3(
  gapi: Object,
  trackAction: (type: string) => void,
  autologin?: boolean = false,
): Promise<$Shape<GoogleResponse>> {
  return _initGoogleButtonV3(gapi, trackAction, autologin);
}

/*
 * The two functions after this are the most basic
 * Google autologin flow we can imagine. Totally
 * okay that this code is mostly duplication;
 * we want to find the drop.
 *   -- Neeraj 5/20/19
 */
function simpleLoad(trackAction: (type: string) => void) {
  const params = {
    scope: GPLUS_SCOPE_PROFILE_EMAIL,
    client_id: GPLUS_CLIENT_ID,
    app_package_name: GPLUS_APP_PACKAGE_NAME,
    access_type: GOOGLE_ACCESS_TYPE,
    cookie_policy: GOOGLE_COOKIE_POLICY,
  };
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  return new Promise<*>((resolve, reject) => {
    trackAction('google_script_fetch');
    loadScriptWithWait('https://apis.google.com/js/platform.js', SCRIPT_TYPE.GPLUS_ONE).then(() => {
      trackAction('google_script_load');
      window.gapi.load('auth2', {
        callback: () => {
          trackAction('google_auth2_load');
          const googleAuth = window.gapi.auth2.init(params);
          googleAuth.then(
            () => {
              trackAction('google_init_success');
              resolve(googleAuth);
            },
            ({ error }) => {
              trackAction('google_init_error');
              reject(`init_exists_${error}`);
            },
          );
        },
        onerror: ({ error }) => {
          trackAction('google_auth2_script_load_error');
          reject(error);
        },
        timeout: 15000, // milliseconds
        ontimeout: () => {
          trackAction('google_auth2_script_load_timeout');
          reject('timeout');
        },
      });
    });
  });
}

export function selfSufficentGoogleAutologin(
  gapi: Object,
  trackAction: (type: string) => void,
): Promise<$Shape<GoogleResponse>> {
  return new Promise((resolve, reject) => {
    if (isAutoLoginDisabled()) {
      reject();
      return;
    }
    trackAction('google_button_init');

    simpleLoad(trackAction).then(
      (googleAuth) => {
        trackAction('google_sdk_load');

        const user = googleAuth.currentUser.get();
        if (!user || !user.Zi) {
          /* user will always be defined, but the Zi property
           * (defined by google) tells us if the object is
           * empty or not
           */
          reject('gplus_user_not_found');
        }
        const authData = user.getAuthResponse(true);

        if (authData) {
          trackAction('google_auth_data_found');
          const result = {
            data: {
              gplus_id_token: authData.id_token,
              gplus_access_token: authData.access_token,
              gplus_expires_at: authData.expires_at,
              gplus_autologin: true,
            },
          };
          resolve(result);
        }
        trackAction('google_auth_data_not_found');
        reject('gplus_user_not_logged_in');
      },
      (error) => {
        trackAction('google_auth2_sdk_init_error');
        reject(error);
      },
    );
  });
}
